<template>
  <div class="typeahead-dropdown-wrapper">
    <div class="typeahead-dropdown">
      <!-- Dropdown Display -->
      <div
        role="combobox"
        aria-controls="dropdown-options"
        aria-haspopup="listbox"
        :class="`dropdown-face ${showOptions ? 'show-options' : ''}`"
        :aria-expanded="showOptions"
        @click="toggleOptions()"
        v-click-outside="
          () => {
            this.closeOptions();
          }
        "
      >
        <input
          type="text"
          class="typeahead-input"
          spellcheck="false"
          v-model="searchQuery"
          :id="`${thisId}-face`"
          :name="name ? name : thisId"
          :aria-label="initialSelect"
          :placeholder="initialSelect"
          @input="debouncedFilterOptions"
          @keydown="handleInputKeydown"
        />
        <button 
          v-if="searchQuery" 
          type="button" 
          class="clear-button" 
          @click="clearInput"
          aria-label="Clear input"
          title="Clear input"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
          </svg>
        </button>
        <svg class="dropdown-arrow">
          <use xlink:href="/assets/public/images/icons.svg#down-arrow" />
        </svg>
      </div>
    </div>

    <!-- Dropdown Options -->
    <ul :id="thisId" class="dropdown-options" v-show="showOptions" role="listbox" aria-label="dropdown-options">
      <li v-for="(option, index) in filteredItems" :key="`${thisId}-${index}`" role="option">
        <input
          type="radio"
          class="non-input-style"
          :title="option.name"
          :id="`${thisId}-${index}`"
          :name="name ? name : thisId"
          :value="option.value || option.name"
          :checked="index === selectedIndex"
          @click="selectOption(option)"
          @keydown="selectOption(option)"
          @change.prevent
        />
        <label :for="`${thisId}-${index}`">{{ option.name }}</label>
      </li>
    </ul>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import { CLICK_OUTSIDE } from '@general/js/_customDirectives';

export default {
  name: 'TypeAheadDropdown',

  props: {
    items: {
      type: Array,
      required: true,
    },
    initialSelect: {
      type: String,
      default: 'Please select one',
    },
    id: {
      type: String,
    },
    name: {
      type: String,
    },
    modelValue: {
      type: String,
      default: '',
    },
  },

  directives: {
    'click-outside': CLICK_OUTSIDE,
  },

  data() {
    return {
      thisId: this.id ? this.id : uuid(),
      searchQuery: '',
      filteredItems: this.items,
      selectedIndex: -1,
      showOptions: false,
    };
  },

  methods: {
    toggleOptions() {
      this.showOptions = !this.showOptions;
    },
    closeOptions() {
      this.showOptions = false;
    },
    selectOption(option) {
      this.searchQuery = option.name;
      this.$emit('update:modelValue', option.value);
      this.closeOptions();
    },
    filterOptions() {
      const query = this.searchQuery.toLowerCase();
      this.filteredItems = this.items.filter((item) => {
        return item.name.toLowerCase().includes(query);
      });
      if(this.filteredItems.length > 0) {
        this.showOptions = true;
      } else {
        this.showOptions = false;
      }
    },
    debouncedFilterOptions: debounce(function() {
      this.filterOptions();
    }, 300),
    handleInputKeydown(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        // Open dropdown if closed and there are items
        if (!this.showOptions && this.filteredItems.length > 0) {
          this.showOptions = true;
          this.selectedIndex = 0;
        } else if (this.selectedIndex < this.filteredItems.length - 1) {
          this.selectedIndex += 1;
        }
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (this.selectedIndex > 0) {
          this.selectedIndex -= 1;
        }
      } else if (event.key === 'Enter' && this.selectedIndex >= 0) {
        event.preventDefault();
        this.selectOption(this.filteredItems[this.selectedIndex]);
      } else if (event.key === 'Escape') {
        this.closeOptions();
      }
    },
    clearInput() {
      this.searchQuery = '';
      this.filteredItems = this.items;
      this.selectedIndex = -1;
      this.$emit('update:modelValue', '');
      this.closeOptions();
    },
  },
};
</script>

<style lang="scss" scoped>

.dropdown-face {
  input {
    padding-right: 4rem;
  }

  .clear-button {
    position: absolute;
    top: 50%;
    right: 2.5rem;
    transform: translateY(-50%);
    cursor: pointer;
    display: flex !important;
    align-items: center;
    justify-content: center;
    z-index: 3;
    width: 2rem !important;
    height: 2rem !important;
    min-width: 2rem !important;
    min-height: 2rem !important;
    border-radius: 1em !important;
    padding: 0 !important;
    margin: 0 !important;
    color: $GREY_600 !important;
    background-color: transparent !important;
    font-size: 1.25rem !important;

    &:hover {
      color: $GREY_800;
      box-shadow: none;
    }

    &:focus {
      outline: 2px solid $PRIMARY_GREEN !important;
      outline-offset: 2px;
      border-radius: 2px;
    }

    svg {
      width: 2rem;
      height: 2rem;
    }
  }
}

.typeahead-input {
  width: 100%;
}
</style>
